<template>
  <v-form ref="form" v-model="valid" @submit.prevent="submit">
    <v-row>
      <v-col cols="12">
        <v-btn-toggle v-model="form.mode" mandatory dense class="mb-2">
          <v-btn value="new">Nueva base de datos</v-btn>
          <v-btn value="existing">Vincular base existente</v-btn>
        </v-btn-toggle>
        <p class="text-caption grey--text mb-0">
          <span v-if="form.mode === 'new'">Crea BD vacía, usuario MySQL nuevo y cuenta web de administrador.</span>
          <span v-else>Registra BD y usuario MySQL ya existentes. No crea credenciales de base de datos.</span>
        </p>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="form.name"
          label="Nombre del negocio *"
          :rules="[v => !!v || 'Requerido']"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="form.address"
          label="Dirección"
          outlined
          dense
        />
      </v-col>

      <template v-if="form.mode === 'new'">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.adminEmail"
            label="Correo del administrador (plataforma) *"
            type="email"
            :rules="newModeRules.email"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.firstName"
            label="Nombre del administrador *"
            :rules="newModeRules.required"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.lastName"
            label="Apellido del administrador *"
            :rules="newModeRules.required"
            outlined
            dense
          />
        </v-col>
      </template>

      <template v-else>
        <v-col cols="12" md="6">
          <v-combobox
            v-model="form.dbName"
            :items="availableDatabases"
            label="Base de datos existente *"
            :loading="loadingDatabases"
            :rules="existingModeRules.dbName"
            hint="Solo BDs no asignadas a otro negocio"
            persistent-hint
            outlined
            dense
            clearable
            @change="onDatabaseChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-combobox
            v-model="form.dbUser"
            :items="databaseUserItems"
            :loading="loadingDbUsers"
            label="Usuario MySQL existente *"
            :rules="existingModeRules.dbUser"
            :disabled="!normalizedDbName"
            hint="Usuarios con privilegios sobre la BD seleccionada"
            persistent-hint
            outlined
            dense
            clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.adminEmail"
            label="Correo de referencia (opcional)"
            type="email"
            hint="Solo informativo; no crea cuenta de plataforma"
            persistent-hint
            outlined
            dense
          />
        </v-col>
      </template>

      <v-col cols="12">
        <SubscriptionFields ref="subscriptionFields" />
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="form.notes"
          label="Notas internas"
          outlined
          dense
          rows="3"
        />
      </v-col>
    </v-row>

    <div class="d-flex justify-end">
      <v-btn text class="mr-2" :disabled="loading" @click="$emit('cancel')">Cancelar</v-btn>
      <v-btn color="primary" type="submit" :loading="loading" :disabled="!valid">
        {{ form.mode === 'existing' ? 'Vincular negocio' : 'Crear negocio' }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { fetchAvailableDatabases, fetchDatabaseUsers } from '@/services/platform/businessService';
import SubscriptionFields from '@/components/platform/SubscriptionFields.vue';

export default {
  name: 'BusinessForm',
  components: { SubscriptionFields },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: false,
      loadingDatabases: false,
      loadingDbUsers: false,
      availableDatabases: [],
      databaseUsers: [],
      form: {
        mode: 'new',
        name: '',
        adminEmail: '',
        firstName: '',
        lastName: '',
        address: '',
        notes: '',
        dbName: '',
        dbUser: '',
      },
      newModeRules: {
        required: [v => !!v || 'Requerido'],
        email: [
          v => !!v || 'Requerido',
          v => /.+@.+\..+/.test(v) || 'Correo inválido',
        ],
      },
      existingModeRules: {
        dbName: [
          v => !!v || 'Seleccione o escriba la base de datos',
          v => /^[a-z0-9_]+$/.test(String(v).trim().toLowerCase()) || 'Solo minúsculas, números y guión bajo',
        ],
        dbUser: [
          v => !!v || 'Seleccione el usuario MySQL',
          v => /^[a-z][a-z0-9_]*$/.test(String(v).trim().toLowerCase()) || 'Usuario MySQL inválido',
        ],
      },
    };
  },
  computed: {
    normalizedDbName() {
      return String(this.form.dbName || '').trim().toLowerCase();
    },
    databaseUserItems() {
      return this.databaseUsers.map((entry) => entry.user);
    },
  },
  watch: {
    'form.mode'(mode) {
      if (mode === 'existing' && !this.availableDatabases.length) {
        this.loadDatabases();
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
  },
  methods: {
    async loadDatabases() {
      this.loadingDatabases = true;
      try {
        this.availableDatabases = await fetchAvailableDatabases();
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingDatabases = false;
      }
    },
    async onDatabaseChange() {
      this.form.dbUser = '';
      this.databaseUsers = [];
      if (!this.normalizedDbName) return;

      this.loadingDbUsers = true;
      try {
        this.databaseUsers = await fetchDatabaseUsers(this.normalizedDbName);
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingDbUsers = false;
      }
    },
    submit() {
      if (!this.$refs.form.validate()) return;

      const subscription = this.$refs.subscriptionFields
        ? this.$refs.subscriptionFields.getPayload()
        : null;

      const payload = {
        mode: this.form.mode,
        name: this.form.name,
        address: this.form.address,
        notes: this.form.notes,
        subscription,
      };

      if (this.form.mode === 'new') {
        payload.adminEmail = this.form.adminEmail;
        payload.firstName = this.form.firstName;
        payload.lastName = this.form.lastName;
      } else {
        payload.dbName = this.normalizedDbName;
        payload.dbUser = String(this.form.dbUser).trim().toLowerCase();
        if (this.form.adminEmail) payload.adminEmail = this.form.adminEmail;
      }

      this.$emit('submit', payload);
    },
  },
};
</script>
